import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, UploadCloud } from "lucide-react";
import { Reveal, staggerContainer, staggerItem } from "./Reveal";
import { Icon } from "./Icon";
import { UploadIcon3D } from "./UploadIcon3D";
import { AmbientOrbs } from "./Ambient";
import { SERVICES } from "@/lib/data";

// Only these categories have a real CMS-backed hub page today; everything
// else still routes to Contact as a lead-gen fallback.
const CATEGORY_HREF = {
  "Business Registration": "/registration",
  "GST Services": "/taxation/gst",
  "Income Tax": "/taxation/income-tax",
  "Corporate Compliance": "/compliance#corporate-compliance",
  "Payroll & Labour Compliance": "/compliance#payroll-labour-compliance",
  "Accounting & Finance": "/accounting-finance",
  "Business Licence & Registration": "/business-licences",
  "Other Services": "/other-services",
};

// Per-item deep links into each service's own CMS detail page — only for
// items that have a real, matching page today. Anything not listed here
// (e.g. Intellectual Property, "Trade Licence") stays plain text.
const ITEM_HREF = {
  "Business Registration": {
    "Private Limited Company": "/registration/private-limited-company",
    "Limited Liability Partnership (LLP)": "/registration/llp",
    "One Person Company (OPC)": "/registration/one-person-company",
    "Partnership Firm": "/registration/partnership-firm",
    "Sole Proprietorship": "/registration/sole-proprietorship",
  },
  "GST Services": {
    "GST Registration": "/taxation/gst/gst-registration",
    "Return Filing": "/taxation/gst/gst-return-filing",
    Amendments: "/taxation/gst/gst-amendment",
    Refunds: "/taxation/gst/gst-refund",
    LUT: "/taxation/gst/lut-registration",
  },
  "Accounting & Finance": {
    Bookkeeping: "/accounting-finance/bookkeeping-accounting",
    "Virtual CFO": "/accounting-finance/virtual-cfo-services",
    "Project Reports": "/accounting-finance/project-reports-cma-data",
    "MIS Reporting": "/accounting-finance/mis-reporting",
  },
  "Business Licence & Registration": {
    Udyam: "/business-licences/udyam-registration",
    "Shop & Est.": "/business-licences/maharashtra-shop-establishment",
    FSSAI: "/business-licences/fssai-registration-licence",
    IEC: "/business-licences/import-export-code-iec",
  },
  "Payroll & Labour Compliance": {
    "PF Registration": "/compliance/pf-epf-compliance",
    ESIC: "/compliance/esi-compliance",
    "Professional Tax": "/compliance/professional-tax-compliance",
    Payroll: "/compliance/payroll-processing-compliance",
    Compliance: "/compliance/labour-law-compliance",
  },
  "Corporate Compliance": {
    "AOC-4": "/compliance/roc-mca-annual-compliance",
    "MGT-7": "/compliance/roc-mca-annual-compliance",
    "DIN KYC": "/compliance/director-din-compliance",
    "DIR Forms": "/compliance/director-din-compliance",
    "Annual Filings": "/compliance/roc-mca-annual-compliance",
  },
  "Income Tax": {
    "ITR Filing": "/taxation/income-tax/income-tax-return-filing",
    "Tax Planning": "/taxation/income-tax/income-tax-planning-advisory",
    "PAN / TAN": "/taxation/income-tax/pan-tan-services",
    "TDS Returns": "/taxation/income-tax/tds-compliance-filing",
    "Tax Notices": "/taxation/income-tax/income-tax-notice-assessment-support",
  },
  "Other Services": {
    "Tax Audit Support": "/other-services/audit-support-services",
    "Internal Audit Support": "/other-services/internal-audit-support",
    "Startup India / DPIIT": "/other-services/startup-india-dpiit-recognition",
    "MSME Advisory": "/other-services/msme-advisory",
  },
};

export const Services = () => (
  <section id="services" className="relative overflow-hidden bg-[rgb(238,242,250)] py-16 md:py-20" data-testid="services-section">
    <AmbientOrbs />
    <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div className="max-w-2xl">
          <Reveal>
            <span className="inline-flex items-center rounded-full border border-emerald2-300/40 bg-emerald2-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-emerald2-600">
              Our Services
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-navy-900 md:text-4xl lg:text-5xl">
              Everything your business needs, in one place
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <Link
            to="/upload"
            data-testid="services-upload-cta"
            className="group relative inline-flex items-center gap-3 overflow-hidden rounded-full bg-gradient-to-r from-[#045ED3] via-[#1E74FD] to-[#0350B4] px-6 py-3 text-sm font-bold text-white shadow-[0_8px_24px_rgba(4,94,211,0.38)] ring-1 ring-white/30 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_32px_rgba(4,94,211,0.55)] active:translate-y-0 active:scale-[0.98]"
          >
            <span className="pointer-events-none absolute inset-0 -translate-x-[160%] skew-x-[-20deg] bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-1000 ease-out group-hover:translate-x-[260%] animate-btn-shimmer" />
            <span className="relative grid h-7 w-7 shrink-0 place-items-center rounded-full bg-white/20 shadow-inner ring-1 ring-white/30 backdrop-blur-sm transition-transform duration-300 group-hover:scale-110 group-hover:bg-white/30">
              <motion.span
                className="relative flex items-center justify-center"
                animate={{ y: [0, -2.5, 0] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              >
                <UploadCloud className="h-4 w-4 text-white" />
              </motion.span>
            </span>
            <span className="relative font-bold tracking-wide">Upload Documents</span>
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald2-300 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
            </span>
          </Link>
        </Reveal>
      </div>

      <motion.div
        variants={staggerContainer}
        initial="hidden"
        whileInView="show"
        viewport={{ once: false, margin: "-60px" }}
        className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4"
      >
        {SERVICES.map((s, i) => {
          const green = i % 2 !== 0;
          return (
            <motion.div
              key={s.title}
              variants={staggerItem}
              className={`group relative flex h-full flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white p-7 shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-2 hover:shadow-[0_20px_44px_rgba(15,30,60,0.12)] ${
                green ? "hover:border-emerald2-300" : "hover:border-gold-300"
              }`}
              data-testid={`service-card-${i}`}
            >
              <Icon
                name={s.icon}
                className={`pointer-events-none absolute bottom-3 right-3 z-0 h-24 w-24 -rotate-12 transition-transform duration-300 group-hover:rotate-0 ${
                  green ? "text-emerald2-500/10" : "text-gold-500/10"
                }`}
              />
              <div className="relative z-10 flex h-full flex-col">
                <motion.span
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut", delay: i * 0.15 }}
                  className={`grid h-12 w-12 place-items-center rounded-2xl transition-transform duration-300 group-hover:-rotate-6 ${
                    green ? "bg-emerald2-600 text-white" : "bg-navy-900 text-gold-400"
                  }`}
                >
                  <Icon name={s.icon} className="h-6 w-6" />
                </motion.span>
                <h3 className="mt-5 text-lg font-semibold text-navy-900">{s.title}</h3>
                <ul className="mt-4 flex-1 space-y-2">
                  {s.items.map((item) => {
                    const href = ITEM_HREF[s.title]?.[item];
                    return (
                      <li key={item} className="flex items-center gap-2 text-sm text-slate-600">
                        <Check className={`h-3.5 w-3.5 shrink-0 ${green ? "text-emerald2-600" : "text-gold-600"}`} />
                        {href ? (
                          <Link to={href} className="transition-colors hover:text-navy-900 hover:underline">
                            {item}
                          </Link>
                        ) : (
                          item
                        )}
                      </li>
                    );
                  })}
                </ul>
                <Link
                  to={CATEGORY_HREF[s.title] || "/contact"}
                  data-testid={`service-learn-more-${i}`}
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-navy-900 transition-colors hover:text-gold-600"
                >
                  Learn more
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  </section>
);
